import { LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { TextInputController } from 'common/components/textInput/TextInput';
import { TextAreaInputController } from 'common/components/textAreaInput/TextAreaInput';
import { Label } from 'common/components/texts/Texts';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { SupplierDto } from 'api/suppliers/models/SupplierDto';
import SuppliersService from 'api/suppliers/SuppliersService';
import { Form } from 'common/components/form/Form';
import { PhoneCodeOption, PhoneInputController } from 'common/components/phoneInput/PhoneInput';
import { CountryDto } from 'api/countries/models/CountryDto';
import CountriesService from 'api/countries/CountriesService';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';

function SupplierScreen (): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<SupplierDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [countries, setCountries] = useState<CountryDto[]>([]);
    const phoneCodesOptions: PhoneCodeOption[] = countries.filter(x => x.phoneCode).map(p => ({ value: p.id, name: p.isoCodeSmall, phoneCode: p.phoneCode }));

    const countriesOptions = useMemo<SelectInputOption[]>(() =>
        countries
            .map(x => ({
                label: t(`countries.${x.isoCodeSmall}`, { defaultValue: x.name }),
                value: x.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    [countries, t]);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['SUPPLIERS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['SUPPLIERS_READ']);

    const url = '/library/suppliers';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    useEffect(() => {
        void getData();
    }, [id, type]);

    const getData = async () => {
        try {
            Loading.show();
            const countriesResult = await CountriesService.getCatalog();
            setCountries(countriesResult);

            let result: Partial<SupplierDto> = { id, name: '' };
            if (id) {
                result = await SuppliersService.getById(id);
            }
            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get supplier information', error);
            Toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/library/suppliers/${typeUrl}/${id}`);
        } else {
            navigate('/library/suppliers');
        }
    };

    const onSubmit = async (model: SupplierDto) => {
        try {
            Loading.show();

            if (model && model.id) {
                await SuppliersService.update(model);
                void getData();
            } else if (model) {
                const id = await SuppliersService.create(model);
                navigateTo('edit', id);
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the supplier with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));

            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }

        Loading.show();
        try {
            await SuppliersService.remove(form.getValues());
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete supplier', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteModal(false);
        }
    };

    const entityName = form.watch('name');
    const titlePrefix = type === 'create'
        ? t('common.new_title.supplier')
        : type === 'edit'
            ? t('common.edit_title.supplier')
            : t('common.details_title.supplier');
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('suppliers.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('suppliers.title'), url },
                    { name: title, url: currentUrl }
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && isDetails && <Button fw href={`/library/suppliers/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name' control={form.control}
                                placeholder={t('suppliers.list.name')} disabled={isDetails}
                                rules={{ required: true, maxLength: 250 }}
                                hasError={Boolean(errors.name)} />
                            <InputError error={errors.name} maxLength={250} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.nif')}</Label>
                            <TextInputController
                                name='nif' control={form.control}
                                placeholder={t('suppliers.list.nif')} disabled={isDetails}
                                rules={{ maxLength: 20 }}
                                hasError={Boolean(errors.nif)} />
                            <InputError error={errors.nif} maxLength={20} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.email')}</Label>
                            <TextInputController
                                name='email' control={form.control}
                                type='email'
                                placeholder={t('suppliers.list.email')} disabled={isDetails}
                                rules={{
                                    maxLength: 250,
                                    pattern: {
                                        value: /^$|^\S+@\S+\.\S+$/,
                                        message: 'email',
                                    }
                                }}
                                hasError={Boolean(errors.email)} />
                            <InputError error={errors.email} maxLength={250} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.phone')}</Label>
                            <PhoneInputController
                                name='phone'
                                control={form.control}
                                onChange={() => {}}
                                disabled={isDetails}
                                phoneCodes={phoneCodesOptions}
                                phoneCodeValueName='phoneCodeId'
                            />
                            <InputError error={errors.phone} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.address')}</Label>
                            <TextInputController
                                name='address' control={form.control}
                                placeholder={t('suppliers.list.address')} disabled={isDetails}
                                rules={{ maxLength: 500 }}
                                hasError={Boolean(errors.address)} />
                            <InputError error={errors.address} maxLength={500} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.postal_code')}</Label>
                            <TextInputController
                                name='postalCode' control={form.control}
                                placeholder={t('suppliers.list.postal_code')} disabled={isDetails}
                                rules={{ maxLength: 20 }}
                                hasError={Boolean(errors.postalCode)} />
                            <InputError error={errors.postalCode} maxLength={20} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.city')}</Label>
                            <TextInputController
                                name='city' control={form.control}
                                placeholder={t('suppliers.list.city')} disabled={isDetails}
                                rules={{ maxLength: 100 }}
                                hasError={Boolean(errors.city)} />
                            <InputError error={errors.city} maxLength={100} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.country')}</Label>
                            <SelectInputController
                                name='countryId'
                                control={form.control}
                                options={countriesOptions}
                                disabled={isDetails}
                            />
                            <InputError error={errors.countryId} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.website')}</Label>
                            <TextInputController
                                name='website' control={form.control}
                                placeholder={t('suppliers.list.website')} disabled={isDetails}
                                rules={{ maxLength: 250 }}
                                hasError={Boolean(errors.website)} />
                            <InputError error={errors.website} maxLength={250} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Label space>{t('suppliers.list.contact_person')}</Label>
                            <TextInputController
                                name='contactPerson' control={form.control}
                                placeholder={t('suppliers.list.contact_person')} disabled={isDetails}
                                rules={{ maxLength: 250 }}
                                hasError={Boolean(errors.contactPerson)} />
                            <InputError error={errors.contactPerson} maxLength={250} />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col xs={12}>
                            <Label space>{t('suppliers.list.notes')}</Label>
                            <TextAreaInputController
                                name='notes' control={form.control}
                                placeholder={t('suppliers.list.notes')} disabled={isDetails}
                                rules={{ maxLength: 2000 }}
                                hasError={Boolean(errors.notes)} />
                            <InputError error={errors.notes} maxLength={2000} />
                        </Col>
                    </Row>
                </PageContainer>
            </Form>
            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default SupplierScreen;
